import { useState } from "react";
import { motion } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Tick02Icon, ArrowRight01Icon } from "@hugeicons/core-free-icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { plans } from "@/components/pricing/PricingCards";

import { cn } from "@/lib/utils";

interface PricingStepProps {
  currentPlan: "free" | "pro" | "max";
  onSelectPlan: (planSlug: "pro" | "max", billing: "monthly" | "annual") => Promise<void>;
  isSelectingPlan: string | null;
}

export function PricingStep({
  currentPlan,
  onSelectPlan,
  isSelectingPlan,
}: PricingStepProps) {
  const [isAnnual, setIsAnnual] = useState(true);

  return (
    <div>
      {/* Header row: title+subtitle on left, billing toggle on right */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-normal text-foreground">
            Do more with Rayloop
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Select a plan based on your needs
          </p>
        </div>

        <div className="bg-foreground/[0.06] dark:bg-foreground/10 relative inline-flex shrink-0 rounded-full p-1">
          <button
            type="button"
            onClick={() => setIsAnnual(false)}
            className={cn(
              "relative z-10 cursor-pointer rounded-full px-5 py-1.5 text-sm font-medium transition-colors duration-200",
              !isAnnual
                ? "text-foreground"
                : "text-muted-foreground/60 hover:text-muted-foreground",
            )}
          >
            {!isAnnual && (
              <motion.span
                layoutId="onboarding-pricing-pill"
                className="bg-background absolute inset-0 rounded-full shadow-sm ring-1 ring-black/[0.08] dark:ring-white/[0.12]"
                transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              />
            )}
            <span className="relative">Monthly</span>
          </button>
          <button
            type="button"
            onClick={() => setIsAnnual(true)}
            className={cn(
              "relative z-10 flex cursor-pointer items-center gap-1.5 rounded-full px-5 py-1.5 text-sm font-medium transition-colors duration-200",
              isAnnual
                ? "text-foreground"
                : "text-muted-foreground/60 hover:text-muted-foreground",
            )}
          >
            {isAnnual && (
              <motion.span
                layoutId="onboarding-pricing-pill"
                className="bg-background absolute inset-0 rounded-full shadow-sm ring-1 ring-black/[0.08] dark:ring-white/[0.12]"
                transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              />
            )}
            <span className="relative">Yearly</span>
            <span className="text-brand relative text-xs font-semibold">
              (save 20%)
            </span>
          </button>
        </div>
      </div>

      {/* Plan cards */}

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        {plans.map((plan) => {
          const planSlug = plan.name.toLowerCase() as "free" | "pro" | "max";
          const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
          const isCurrentPlan = currentPlan === planSlug;
          const isPaid = planSlug !== "free";

          return (
            <div
              key={plan.name}
              className={cn(
                "relative flex flex-col rounded-2xl p-5 ring-1 transition-all duration-200",
                isCurrentPlan
                  ? "ring-foreground bg-foreground/[0.02]"
                  : plan.highlighted
                    ? "ring-foreground/20"
                    : "ring-border",
              )}
            >
              {plan.highlighted && (
                <Badge className="bg-foreground text-background absolute -top-2.5 left-5 border-none text-xs">
                  Most Popular
                </Badge>
              )}

              <h3 className="text-lg font-semibold text-foreground">
                {plan.name}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {plan.description}
              </p>

              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-3xl text-foreground">
                  ${price}
                </span>
                <span className="text-sm text-muted-foreground">/month</span>
                {isAnnual && plan.monthlyPrice > 0 && (
                  <span className="text-muted-foreground/50 ml-auto text-xs">
                    Billed annually
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm font-medium text-brand">
                {plan.credits}
              </p>

              {/* CTA button */}
              {!isPaid || isCurrentPlan ? (
                <Button
                  variant="outline"
                  className="mt-4 w-full cursor-default opacity-60"
                  disabled
                >
                  Current plan
                </Button>
              ) : (
                <Button
                  className="mt-4 w-full gap-2 cursor-pointer bg-foreground text-background hover:bg-foreground/85 border-none transition-all duration-150"
                  disabled={isSelectingPlan !== null}
                  onClick={() =>
                    onSelectPlan(
                      planSlug as "pro" | "max",
                      isAnnual ? "annual" : "monthly",
                    )
                  }
                >
                  {isSelectingPlan === planSlug ? (
                    "Redirecting…"
                  ) : (
                    <>
                      Select plan
                      <HugeiconsIcon
                        icon={ArrowRight01Icon}
                        className="size-4"
                      />
                    </>
                  )}
                </Button>
              )}

              <ul className="mt-4 space-y-2">
                {plan.features.map((feature) => (
                  <li
                    key={feature.text}
                    className="flex items-start gap-2 text-sm"
                  >
                    <HugeiconsIcon
                      icon={Tick02Icon}
                      className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                    />
                    <span className="text-foreground/80">{feature.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

    </div>
  );
}
